import React, { useMemo } from "react";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import { Flex, Button, ModalV2, Text } from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import { RiskRating as RiskRatingEnum } from "../../../graphql/supergraphEnums";
import { RiskRating } from "../../../graphql/supergraphTypes";
import { PositionName } from "../../common/PositionName";
import { RiskRatingBadge } from "../RiskRatingBadge";
import {
  borderStyle,
  ratingCategory,
  ratingItem,
  ratingItemHeader,
  ratingTableWrapper,
  ratingValue,
} from "./styles_vanilla.css";

export type PositionRiskRatings = {
  bankAccountDomicile?: RiskRating | null;
  paymentBeneficiary?: RiskRating | null;
  investor?: RiskRating | null;
  position?: RiskRating | null;
};

export type RiskRatingDetailsModalProps = {
  show: boolean;
  onClose: () => void;
  ratings: PositionRiskRatings;
  positionName?: string | null;
  entityName?: string | null;
};

const CATEGORIES: Array<{ label: string; key: keyof PositionRiskRatings }> = [
  { label: "Bank account domicile", key: "bankAccountDomicile" },
  { label: "Payment beneficiary", key: "paymentBeneficiary" },
  { label: "Investor", key: "investor" },
  { label: "Position", key: "position" },
];

export const RiskRatingDetailsModal: React.FC<RiskRatingDetailsModalProps> = ({
  show,
  onClose,
  ratings,
  positionName,
  entityName,
}) => {
  const rows = useMemo(
    () =>
      CATEGORIES.map((category) => ({
        label: category.label,
        rating: ratings[category.key] ?? RiskRatingEnum.None,
      })),
    [ratings],
  );

  return (
    <ModalV2
      show={show}
      onHide={onClose}
      size="large"
      title="Risk rating calculation"
      description={<PositionName account={positionName} entity={entityName} />}
    >
      <ModalV2.Body>
        <Flex className={ratingTableWrapper}>
          <Flex className={ratingItemHeader}>
            <div className={ratingCategory}></div>
            <Text variant="strong-md" className={ratingValue}>
              Current risk rating
            </Text>
          </Flex>
          {rows.map((row, index) => (
            <Flex
              key={row.label}
              className={ratingItem}
              style={assignInlineVars({
                [borderStyle]:
                  index === rows.length - 1
                    ? `1px solid ${Colors.Neutral800}`
                    : `1px solid ${Colors.Neutral400}`,
              })}
            >
              <Text variant="strong-md" className={ratingCategory}>
                {row.label}
              </Text>
              <div className={ratingValue}>
                <RiskRatingBadge rating={row.rating} />
              </div>
            </Flex>
          ))}
        </Flex>
      </ModalV2.Body>
      <ModalV2.Footer>
        <Flex flexDirection="row" flexWrap="nowrap" justifyContent="flex-end">
          <Button variant="primary" onClick={onClose} data-testid="risk-rating-modal-close">
            Close
          </Button>
        </Flex>
      </ModalV2.Footer>
    </ModalV2>
  );
};
